// Pure, deterministic PickHouse Showdown playoff bracket engine. No side effects, no I/O.
// Mock winner rule: the higher regular-season seed (lower number) advances.
export const MAX_FIELD = 16;

// standings: array already ordered best->worst. Returns top-16 seeded 1..N.
export function qualifyField(standings) {
  return standings.slice(0, MAX_FIELD).map((p, i) => ({ ...p, seed: i + 1 }));
}

export function byeCountFor(n) {
  return Math.max(0, MAX_FIELD - n); // 16 - n highest seeds get a Wild Card bye
}

const win = (a, b) => (a.seed <= b.seed ? a : b);
const mkGame = (a, b) => ({ a, b, winner: win(a, b) });

// field: seeded qualifiers (1..N, N between 8 and 16). Returns full deterministic bracket.
export function buildBracket(field) {
  const n = field.length;
  if (n < 8) {
    return { error: `Only ${n} qualified player${n === 1 ? "" : "s"} — a standard PickHouse bracket needs at least 8. STOP: this edge case needs approval before inventing a new format.` };
  }
  const byeCount = byeCountFor(n); // 16 - n
  const byes = field.slice(0, byeCount);
  const playing = field.slice(byeCount);

  // Wild Card: pair remaining seeds inward (highest vs lowest)
  const wildcard = [];
  let lo = 0, hi = playing.length - 1;
  while (lo < hi) { wildcard.push(mkGame(playing[lo], playing[hi])); lo++; hi--; }

  // 8 divisional entrants = byes + wild-card winners, RESEEDED by original seed
  const divEntrants = [...byes, ...wildcard.map((g) => g.winner)].sort((x, y) => x.seed - y.seed);
  const r = divEntrants; // r[0]=best ... r[7]=worst of the 8

  // Two balanced, deterministic sides (standard 8-seed bracket split; keeps #1 & #2 apart)
  const sideA = { name: "Side A", div: [mkGame(r[0], r[7]), mkGame(r[3], r[4])] };
  const sideB = { name: "Side B", div: [mkGame(r[1], r[6]), mkGame(r[2], r[5])] };
  sideA.conf = mkGame(sideA.div[0].winner, sideA.div[1].winner);
  sideB.conf = mkGame(sideB.div[0].winner, sideB.div[1].winner);
  sideA.finalist = sideA.conf.winner;
  sideB.finalist = sideB.conf.winner;

  const superBowl = mkGame(sideA.finalist, sideB.finalist);
  return { n, byeCount, byes, wildcard, divEntrants, sideA, sideB, superBowl, champion: superBowl.winner };
}

// "If the playoffs started today" projection for one viewer seed.
export function projectForSeed(field, seed) {
  const n = field.length;
  const byeCount = byeCountFor(n);
  const inField = seed >= 1 && seed <= n;
  if (!inField) return { inField: false };
  const hasBye = seed <= byeCount;
  let opponentSeed = null;
  if (!hasBye) {
    // playing seeds are (byeCount+1)..n paired inward: opp = (byeCount+1) + n - seed
    opponentSeed = byeCount + 1 + n - seed;
  }
  const opp = opponentSeed ? field.find((p) => p.seed === opponentSeed) : null;
  return { inField: true, seed, hasBye, opponent: opp };
}
